import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const StrengthPill = ({ label }) => (
  <span className="mt-2 inline-block rounded-full bg-aegean/10 px-3 py-1 text-xs font-semibold text-aegean">
    {label}
  </span>
);

export const ArgumentsTables = () => {
  const { d, ui } = useLang();
  return (
    <div className="my-8 space-y-8" data-testid="arguments-tables">
      {Object.values(d.ARGUMENTS).map((arg) => (
        <div key={arg.title} className="overflow-hidden rounded-lg border border-line bg-paper">
          <div className="border-b border-line bg-surface px-6 py-4">
            <h4 className="font-serif text-xl font-semibold text-ink">{arg.title}</h4>
          </div>
          <div className="grid gap-0 md:grid-cols-2">
            <div className="border-b border-line p-6 md:border-b-0 md:border-r">
              <p className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-aegean">
                <CheckCircle2 className="h-4 w-4" /> {ui.strengthsLabel}
              </p>
              <ul className="space-y-4">
                {arg.strengths.map((s, i) => (
                  <li key={i} className="text-sm leading-relaxed text-foreground/90">
                    {s.text}
                    {s.strength && <StrengthPill label={`${ui.strengthPrefix} ${s.strength}`} />}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-6">
              <p className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-terra">
                <XCircle className="h-4 w-4" /> {ui.weaknessesLabel}
              </p>
              <ul className="space-y-4">
                {arg.weaknesses.map((w, i) => (
                  <li key={i} className="text-sm leading-relaxed text-foreground/90">{w.text}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const CoordsTable = () => {
  const { d } = useLang();
  const COORDS = d.COORDS;
  return (
    <div className="my-8" data-testid="coords-table-wrap">
      {/* Mobile: κάρτες — χωρίς οριζόντια κύλιση */}
      <div className="space-y-3 md:hidden" data-testid="coords-cards">
        {COORDS.map((c, i) => (
          <div key={c.island} className="rounded-lg border border-line bg-paper p-4" data-testid={`coords-card-${i}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-serif text-lg font-semibold text-ink">{c.island}</p>
              <p className="text-sm font-semibold text-aegean">{c.lat}</p>
            </div>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{c.note}</p>
          </div>
        ))}
      </div>
      {/* Desktop: πίνακας */}
      <div className="hidden overflow-x-auto rounded-lg border border-line md:block" data-testid="coords-table">
        <table className="w-full min-w-[560px] border-collapse text-left">
          <thead>
            <tr className="bg-aegean text-paper">
              <th className="px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]">Island</th>
              <th className="px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]">Latitude</th>
              <th className="px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]">Note</th>
            </tr>
          </thead>
          <tbody>
            {COORDS.map((c, i) => (
              <tr key={c.island} className={i % 2 === 0 ? 'bg-paper' : 'bg-surface/60'}>
                <td className="px-5 py-4 font-serif text-lg font-semibold text-ink">{c.island}</td>
                <td className="px-5 py-4 text-sm font-semibold text-aegean">{c.lat}</td>
                <td className="px-5 py-4 text-sm text-muted-foreground">{c.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const NamingTable = () => {
  const { d, lang } = useLang();
  const NAMING_SYSTEMS = d.NAMING_SYSTEMS;
  const nameHeader = lang === 'en' ? 'Homeric name' : 'Ομηρικό όνομα';
  return (
    <div className="my-8" data-testid="naming-table">
      {/* Mobile: κάρτες ανά όνομα — χωρίς οριζόντια κύλιση */}
      <div className="space-y-3 md:hidden" data-testid="naming-cards">
        {NAMING_SYSTEMS.names.map((name, i) => (
          <div key={name} className="rounded-lg border border-line bg-paper p-4" data-testid={`naming-card-${i}`}>
            <p className="font-serif text-lg font-semibold text-ink">{name}</p>
            <div className="mt-2 space-y-2">
              {NAMING_SYSTEMS.columns.map((c) => (
                <div key={c.key} className="border-t border-line pt-2">
                  <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink/60">{c.label}</p>
                  <p className="mt-0.5 text-sm leading-relaxed text-foreground/90">{NAMING_SYSTEMS.rows[name][c.key]}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Desktop: πίνακας */}
      <div className="hidden overflow-x-auto rounded-lg border border-line md:block" data-testid="naming-table-desktop">
        <table className="w-full min-w-[640px] border-collapse text-left">
          <thead>
            <tr className="bg-aegean text-paper">
              <th className="px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]">{nameHeader}</th>
              {NAMING_SYSTEMS.columns.map((c) => (
                <th key={c.key} className="px-5 py-3 text-xs font-bold uppercase tracking-[0.15em]">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {NAMING_SYSTEMS.names.map((name, i) => (
              <tr key={name} className={i % 2 === 0 ? 'bg-paper' : 'bg-surface/60'}>
                <td className="px-5 py-4 font-serif text-lg font-semibold text-ink">{name}</td>
                {NAMING_SYSTEMS.columns.map((c) => (
                  <td key={c.key} className="px-5 py-4 text-sm leading-relaxed text-foreground/90">
                    {NAMING_SYSTEMS.rows[name][c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-4 border-l-2 border-terra pl-4 text-sm leading-relaxed text-muted-foreground">
        {NAMING_SYSTEMS.footnote}
      </p>
    </div>
  );
};

export const MovesTable = () => {
  const { d } = useLang();
  return (
    <div className="my-8 overflow-hidden rounded-lg border border-line" data-testid="moves-table">
      {d.MOVES.map((m, i) => (
        <div
          key={i}
          className={`grid gap-2 px-6 py-5 md:grid-cols-[180px_1fr_1fr] md:items-center ${i % 2 === 0 ? 'bg-paper' : 'bg-surface/60'}`}
        >
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-aegean">{m.theory}</p>
          <p className="flex items-center gap-2 font-serif text-lg font-semibold text-ink">
            {m.move.includes('→') ? (
              <>
                {m.move.split('→')[0].trim()}
                <ArrowRight className="h-4 w-4 shrink-0 text-terra" />
                {m.move.split('→')[1].trim()}
              </>
            ) : (
              m.move
            )}
          </p>
          <p className="text-sm leading-relaxed text-muted-foreground">{m.result}</p>
        </div>
      ))}
    </div>
  );
};
